import { useLoaderData } from 'react-router';
import { useHydrated } from 'remix-utils';

import { useAppDisplayModeContext } from '@archipro-design/aria';
import { DetailMobileAppChrome } from '~/ui-rocco/pages/detail-shared';
import { BackToTop } from '~/ui-rocco/components/BackToTop';
import { SectionSpacer } from '~/ui-rocco/pages/home-page/components/SectionSpacer';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import { BookMeetingAccordionProvider } from './hooks/use-book-meeting-accordion';
import { ProjectDetailsMagicIsland } from './magic-island/ProjectDetailsMagicIsland';
import { ProjectHeroSection } from './sections/ProjectHeroSection';
import {
    ProjectStickyActions,
    ProjectSummarySection,
} from './sections/ProjectSummarySection';
import { ProjectBackdropMark } from './sections/ProjectBackdropMark';
import { ProjectBreadcrumbBar } from './sections/ProjectBreadcrumbBar';
import { ProjectCreditsRow } from './sections/ProjectCreditsRow';
import { ProjectStorySection } from './sections/ProjectStorySection';
import { AboutProjectSection } from './sections/AboutProjectSection';
import { ProjectHireSection } from './sections/ProjectHireSection';
import { ProjectTestimonialSection } from './sections/ProjectTestimonialSection';
import { ProjectSourcebookSection } from './sections/ProjectSourcebookSection';
import { NextProjectTransition } from './sections/NextProjectTransition';
import { ProjectGallerySection } from './sections/ProjectGallerySection';
import { MoreProjectsFromSection } from './sections/MoreProjectsFromSection';
import { MoreInProjectCategorySection } from './sections/MoreInProjectCategorySection';
import { ProjectProfessionalsUsedSection } from './sections/ProjectUsedSections';

/**
 * Revamped editorial project details page.
 *
 * Built section-by-section (P2–P10) per
 * `plans/BC-4505-details-page-revamp.md` → "Project details — section breakdown".
 * Sections are added incrementally, top to bottom; the loader is shared with the
 * legacy page and stays unchanged. Rendered in place of the legacy page behind
 * the `revamp_2026` feature flag (or `?revamp=1` in local dev).
 */
export const ProjectPageRevamp = () => {
    const { project } = useLoaderData<typeof loader>();
    const hydrated = useHydrated();
    const { mobile } = useAppDisplayModeContext();
    // The featured editorial (ProjectStorySection) is optional. Only widen the
    // lead-in to the design's L gap when it will actually render — otherwise the
    // credits → accordion gap stays at the standard spacing (no empty band).
    const hasFeaturedArticle = Boolean(
        (
            project as typeof project & {
                FeaturedArticle?: { Link: string } | null;
            }
        ).FeaturedArticle,
    );

    return (
        <BookMeetingAccordionProvider>
            <ProjectDetailsMagicIsland>
                <DetailMobileAppChrome />
                <main
                    id="main-content"
                    data-testid="project-page-revamp"
                    // overflow-x-clip contains the gallery modules' 4px full-bleed
                    // so it never triggers a horizontal scrollbar (it clips
                    // without becoming a scroll container, so the fixed island and
                    // vertical scroll are unaffected).
                    // `focus-ring-on-dark` flips the inherited focus-ring
                    // token for the whole page: this ground is the same
                    // near-black as the default ring, so keyboard rings have to
                    // read off-white here (the white closing resets it).
                    className="overflow-x-clip bg-[#181818] focus-ring-on-dark"
                >
                    {/* Sections P2–P10 land here, top to bottom. The content block
                    sits on the dark editorial background; the hero is a
                    full-bleed image and the closing brings its own sections. */}
                    <ProjectHeroSection />
                    {/* The product page's 80px between its trail and its
                        summary row. */}
                    <SectionSpacer height={80} mobileHeight={24} unit="rem" />
                    {/* Book a Meeting, Visit Website and Save: level with the
                        summary's first line and sticky from there, so they
                        ride the whole page. Here in the main column, above
                        every layer below, because a sticky element only
                        travels as far as its parent. */}
                    <ProjectStickyActions />
                    {/* The band under the banner carries the big soft "A"
                        behind it. Positioned here rather than inside the
                        section so the mark can be absolute against the whole
                        band without the section having to know about it. */}
                    <div className="relative">
                        <ProjectBackdropMark />
                        <div className="relative z-10">
                            <ProjectSummarySection />
                        </div>
                    </div>
                    {/*
                     * Everything below the band, lifted onto its own layer.
                     *
                     * The band is positioned, so it paints after every
                     * unpositioned block that follows it — which put the
                     * backdrop mark, tall enough to reach this far and drifting
                     * further on scroll, on top of the credits and the
                     * accordion's rules. Positioning the rest of the page above
                     * it is what puts the mark behind the page rather than over
                     * it; clipping it to the band instead would cut off more
                     * than half the letterform.
                     */}
                    <div className="relative z-10">
                        <SectionSpacer
                            height={64}
                            mobileHeight={24}
                            unit="rem"
                        />
                        {/* The phone's trail, as the credits row's address
                            line — the hero's own bar stands down below md. */}
                        <ProjectBreadcrumbBar />
                        <ProjectCreditsRow />
                        {/* Generous L lead-in to the featured editorial — only when
                        that (optional) section renders; otherwise the standard
                        64px gap to the accordion. */}
                        <SectionSpacer
                            height={hasFeaturedArticle ? 160 : 64}
                            mobileHeight={hasFeaturedArticle ? 59 : 32}
                            unit="rem"
                        />
                        <ProjectStorySection />
                        {/* 176 above the About block, counting the 64 that
                            precedes the (optional) story, and the same 176
                            below it. */}
                        <SectionSpacer
                            height={112}
                            mobileHeight={56}
                            unit="rem"
                        />
                        <AboutProjectSection />
                        <SectionSpacer
                            height={176}
                            mobileHeight={88}
                            unit="rem"
                        />
                        <ProjectGallerySection />
                        <SectionSpacer
                            height={120}
                            mobileHeight={48}
                            unit="rem"
                        />
                        {/* Mobile folds the related carousel into About-the-
                        Professional, so this gap collapses there (the next
                        spacer carries the gap down to Similar Projects). */}
                        <SectionSpacer
                            height={120}
                            mobileHeight={0}
                            unit="rem"
                        />
                        {/* The product page's two-card offer — the
                        practice's website and an enquiry to them — then what
                        went into the project and who built it, before the
                        page turns to other work. From here down the page
                        closes the way the article does: its section heads in
                        the editorial voice, 180px apart. */}
                        <ProjectHireSection />
                        {/* Each of these three draws its own gap above it —
                            what has been said about the practice at the
                            product page's 120, the sourcebook at the same 240
                            the invitation has, the practices at 180 — because
                            all three render nothing for a project without the
                            data, and a gap left behind here stacked with its
                            neighbours into one empty run. */}
                        <ProjectTestimonialSection />
                        <ProjectSourcebookSection />
                        <ProjectProfessionalsUsedSection />
                        <SectionSpacer
                            height={180}
                            mobileHeight={80}
                            unit="rem"
                        />
                        <MoreProjectsFromSection />
                        <SectionSpacer
                            height={180}
                            mobileHeight={80}
                            unit="rem"
                        />
                        {/* Keyed on the project: this route does not remount
                        between projects, and clicking a tile in this very grid
                        lands on another project in the same category, so its
                        paging state has to be tied to the project id. */}
                        <MoreInProjectCategorySection key={project.ID} />
                        <SectionSpacer
                            height={200}
                            mobileHeight={80}
                            unit="rem"
                        />
                        {/* The professional's next project, growing into
                            its own page as the reader reaches the foot. Not
                            on mobile: a flick's momentum keeps scrolling
                            through the input hold, so the turn lands the
                            next page already mid-scroll. */}
                        {!mobile && <NextProjectTransition />}
                    </div>
                </main>
                {/* Long page — same control as the browse grids and the
                    product detail page. Left dark: the control takes its
                    page's own tone, and this page's ground is #181818. */}
                {hydrated && !mobile ? <BackToTop overDark /> : null}
            </ProjectDetailsMagicIsland>
        </BookMeetingAccordionProvider>
    );
};
